import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnDestroy,
  PLATFORM_ID,
  inject,
  signal,
  computed,
  viewChild,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { brandColor } from '../../services/theme';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';

import {
  PaymentService,
  RazorpayCheckout,
  postToGateway,
  gatewayLabel,
  openGatewayWindow,
  GATEWAY_WINDOW_NAME,
} from '../../services/payment';
import { RazorpayService, RazorpaySuccess } from '../../services/razorpay';
import { ToastService } from '../../services/toast';
import { MemberEmailDialog } from '../../components/member-email-dialog/member-email-dialog';

/** A nominee row for the review summary. */
export interface SummaryNominee {
  name: string;
  relationship: string;
  dob: string;
  percentage: number;
  mobile: string;
}

/** Everything the payment review step shows — built by the wizard from its form. */
export interface MemberPaymentSummary {
  policyName: string;
  coverageType: string;
  premiumCode: string;
  fullName: string;
  dob: string;
  gender: string;
  mobile: string;
  email: string;
  pan: string;
  address: string;
  // premium breakup
  base: number;
  doctor: number;
  gstPer: number;
  gst: number;
  total: number;
  nominees: SummaryNominee[];
}

/**
 * Step 4 of the member-onboarding wizard — review & pay.
 *
 * Shows the member's important details, nominees and the premium breakup, then
 * collects the premium via Razorpay. The actual method (UPI / card / …) is chosen
 * inside Checkout, so there's no payment-mode picker here. Payer is always the
 * PARTNER and the Client Reference auto-fills the member's unique id — both sent
 * to the backend, neither shown.
 */
@Component({
  selector: 'app-member-payment-step',
  standalone: true,
  imports: [CommonModule, ButtonModule, TagModule, MemberEmailDialog],
  templateUrl: './member-payment-step.html',
  styleUrl: './member-payment-step.css',
})
export class MemberPaymentStep implements OnInit, OnDestroy {
  private payment = inject(PaymentService);
  private rzp = inject(RazorpayService);
  private toast = inject(ToastService);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);

  /** Member created earlier in the wizard. */
  @Input({ required: true }) memberId!: number;
  /** Member unique id — used as the auto Client Reference. */
  @Input() memberUniqueId: string | null = null;
  /** Charge amount = the calculated gross premium. */
  @Input() amount: number | null = null;
  /** Review summary (details + nominees + breakup). */
  @Input() summary: MemberPaymentSummary | null = null;
  /** Razorpay Checkout prefill. */
  @Input() memberName: string | null = null;
  @Input() memberEmail: string | null = null;
  @Input() memberMobile: string | null = null;

  /** Parent resets the wizard to onboard another member. */
  @Output() restart = new EventEmitter<void>();

  processing = signal(false);
  done = signal(false);

  /** Opened when a gateway refuses the payment for want of an e-mail. */
  private emailDialog = viewChild(MemberEmailDialog);

  /** True while the customer is paying in the redirect gateway's popup. */
  awaitingGateway = signal(false);
  private gatewayWindow: Window | null = null;
  private pollTimer: any = null;

  private static readonly POLL_MS = 5000;
  private static readonly POLL_LIMIT_MS = 8 * 60 * 1000;
  /** Onboarded but premium deferred (Pay Later) — a first-class outcome, member stays UNPAID. */
  savedLater = signal(false);
  /** The method Razorpay reported (captured by the backend) — shown on the done screen. */
  paidMode = signal<string | null>(null);
  private paymentUid = '';

  /** Which gateway will actually open, so the button can name it instead of assuming Razorpay. */
  gateway = signal<string>('');
  gatewayRedirect = signal(false);

  payLabel = computed(() => {
    const name = gatewayLabel(this.gateway());
    return name ? `Pay with ${name}` : 'Pay now';
  });

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.rzp.load(); // warm the Checkout script

    if (this.memberId > 0) {
      this.payment.payMethod(this.memberId).subscribe({
        next: (res: any) => {
          const d = res?.data;
          if (!d) return;
          this.gateway.set(d.gateway ?? '');
          this.gatewayRedirect.set(!!d.redirect);
        },
        // Unknown gateway just means a name-free button — never block paying over it.
        error: () => {},
      });
    }
  }

  // ===================== pay =====================
  pay() {
    const amt = Number(this.amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      this.toast.warning('Premium amount is missing. Please review the member details.');
      return;
    }
    // The gateway is the SERVER's decision, from the partner's assignment. Posting straight to
    // Razorpay here is why assigning a partner the Generali integration appeared to do nothing:
    // the onboarding wizard had already picked before the assignment was ever consulted.
    this.processing.set(true);

    // Opened NOW, not when the response lands: a window.open from an HTTP callback is
    // unsolicited and gets blocked. A redirect gateway runs inside it so the wizard stays on
    // screen behind the payment; Razorpay doesn't need it and it is closed again below.
    this.gatewayWindow = openGatewayWindow();

    this.payment.payStart({ memberId: this.memberId }).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.closeGatewayWindow();
          this.processing.set(false);

          // The gateway needs an e-mail this member doesn't have. Offer to add it right here —
          // the wizard has already created the member, so the alternative is abandoning a
          // finished onboarding over one missing field.
          if (res?.data?.needsEmail) {
            this.emailDialog()?.open();
            return;
          }

          this.toast.error(res?.message || 'Could not start the payment.');
          return;
        }
        const d = res?.data ?? {};

        if (String(d.gateway || '').toUpperCase() === 'GENERALI') {
          this.openGenerali(d);
          return;
        }

        this.closeGatewayWindow();
        this.openCheckout(d as RazorpayCheckout);
      },
      error: () => {
        this.closeGatewayWindow();
        this.processing.set(false);
        this.toast.error('Could not start the payment.');
      },
    });
  }

  /**
   * Generali is a REDIRECT gateway — the customer pays on Generali's own page, which cannot be
   * framed. So it runs in a popup and the wizard watches the payment settle behind it, which
   * gives the same in-place feel as Razorpay's modal without pretending the page can be embedded.
   *
   * The gateway's own callback confirms and issues server-side regardless; the polling here only
   * decides what this screen shows.
   */
  private openGenerali(d: { postUrl?: string; fields?: Record<string, string>; paymentUniqueId?: string }) {
    const target = this.gatewayWindow ? GATEWAY_WINDOW_NAME : undefined;

    if (!postToGateway(d?.postUrl, d?.fields, target)) {
      this.closeGatewayWindow();
      this.processing.set(false);
      this.toast.error('The payment gateway did not return a valid redirect.');
      return;
    }

    this.paymentUid = d.paymentUniqueId ?? '';

    // Popup blocked: the form went to this tab and we are navigating away. The customer returns
    // through /payments/result, so there is nothing left for this screen to watch.
    if (!this.gatewayWindow) return;

    this.awaitingGateway.set(true);
    this.watchGatewayPayment();
  }

  /**
   * Each poll may make the server ask Generali directly, so it is deliberately unhurried and
   * capped — an abandoned window must not leave us querying the gateway indefinitely.
   */
  private watchGatewayPayment() {
    this.clearGatewayPoll();
    let elapsed = 0;

    this.pollTimer = setInterval(() => {
      elapsed += MemberPaymentStep.POLL_MS;
      // A window shut a moment AFTER paying still deserves one last look.
      const stop = elapsed >= MemberPaymentStep.POLL_LIMIT_MS || !!this.gatewayWindow?.closed;
      if (stop) this.clearGatewayPoll();
      this.checkGatewayPayment(stop);
    }, MemberPaymentStep.POLL_MS);
  }

  private checkGatewayPayment(final: boolean) {
    if (!this.paymentUid) {
      if (final) this.giveUpOnGateway();
      return;
    }

    this.payment.payGetByUid(this.paymentUid).subscribe({
      next: (res: any) => {
        const p = res?.data;
        const status = String(p?.paymentStatus ?? '').toUpperCase();

        if (status === 'SUCCESS') {
          this.clearGatewayPoll();
          this.closeGatewayWindow();
          this.processing.set(false);
          this.awaitingGateway.set(false);
          if (p?.paymentMode) this.paidMode.set(p.paymentMode);
          this.done.set(true);
          this.toast.popup('Member onboarded and payment received.', 'All done', 'success');
          return;
        }

        if (status === 'FAILED' || status === 'CANCELLED') {
          this.clearGatewayPoll();
          this.closeGatewayWindow();
          this.processing.set(false);
          this.awaitingGateway.set(false);
          this.toast.error(p?.failureReason || 'The payment was not completed.');
          return;
        }

        if (final) this.giveUpOnGateway();
      },
      error: () => {
        if (final) this.giveUpOnGateway();
      },
    });
  }

  /** Still unsettled when we stopped watching — never imply nothing was charged. */
  private giveUpOnGateway() {
    this.clearGatewayPoll();
    this.processing.set(false);
    this.awaitingGateway.set(false);
    this.toast.info(
      'The payment window closed before the payment was confirmed. If money was debited it will ' +
        'confirm shortly — do NOT collect again; check the member before retrying.',
    );
  }

  private clearGatewayPoll() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private closeGatewayWindow() {
    try {
      this.gatewayWindow?.close();
    } catch {
      /* already gone, or cross-origin once the gateway navigated it */
    }
    this.gatewayWindow = null;
  }

  /** The customer is mid-payment in the popup — bring them back to it. */
  focusGatewayWindow() {
    try {
      this.gatewayWindow?.focus();
    } catch {
      /* nothing to focus */
    }
  }

  /** The e-mail was saved — carry straight on with the payment that needed it. */
  onEmailSaved(email: string) {
    this.memberEmail = email;
    this.pay();
  }

  ngOnDestroy() {
    this.clearGatewayPoll();
  }

  private openCheckout(co: RazorpayCheckout) {
    if (!co?.orderId || !co?.keyId) {
      this.processing.set(false);
      this.toast.error('Payment could not be initialised.');
      return;
    }
    this.paymentUid = co.paymentUniqueId;
    this.rzp.load().then((ok) => {
      if (!ok) {
        this.processing.set(false);
        this.toast.error('Could not load Razorpay Checkout. Check your connection.');
        return;
      }
      this.rzp.open({
        key: co.keyId,
        order_id: co.orderId,
        amount: co.amount,
        currency: co.currency,
        name: co.name,
        description: co.description,
        prefill: { name: this.memberName, email: this.memberEmail, contact: this.memberMobile },
        theme: { color: brandColor() },
        handler: (r) => this.verify(r),
        modal: { ondismiss: () => this.onDismiss() },
        onFailed: (e) => {
          this.processing.set(false);
          this.toast.error(e?.error?.description || 'Payment failed.');
        },
      });
    });
  }

  private verify(r: RazorpaySuccess) {
    this.payment
      .rzpVerify({
        razorpayOrderId: r.razorpay_order_id,
        razorpayPaymentId: r.razorpay_payment_id,
        razorpaySignature: r.razorpay_signature,
      })
      .subscribe({
        next: (res: any) => {
          this.processing.set(false);
          const memberStatus = res?.data?.memberPaymentStatus ?? res?.data?.MemberPaymentStatus;
          if (res?.success && memberStatus === 'PAID') {
            this.done.set(true);
            this.toast.popup('Member onboarded and payment received.', 'All done', 'success');
            this.fetchPaidMode();
          } else {
            this.toast.error(res?.message || 'Payment verification failed.');
          }
        },
        error: () => {
          this.processing.set(false);
          // The gateway already took the money. Never imply otherwise — give the
          // payment id so support can reconcile instead of collecting twice.
          this.toast.error(
            `Payment WAS taken (ref ${r.razorpay_payment_id}) but could not be confirmed. ` +
              'Do NOT collect again — quote this reference to support.',
          );
        },
      });
  }

  private onDismiss() {
    this.processing.set(false);
    this.toast.info('Payment not completed. If money was debited it will confirm shortly.');
  }

  /** Read back the payment to show the method the gateway reported (UPI / card / …). */
  private fetchPaidMode() {
    if (!this.paymentUid) return;
    this.payment.payGetByUid(this.paymentUid).subscribe({
      next: (res: any) => {
        const mode = res?.data?.paymentMode ?? res?.data?.PaymentMode;
        if (mode) this.paidMode.set(mode);
      },
    });
  }

  // ===================== pay later =====================
  /**
   * Defer collection. The member is already created and stays UNPAID — nothing to call;
   * we just confirm the outcome so the operator knows onboarding succeeded and where to
   * collect later. The member appears in the pending lists (partner records / payments).
   */
  payLater() {
    this.savedLater.set(true);
    this.toast.success('Member onboarded. Collect the premium later from Payments → Collect.');
  }

  /** From the Pay Later screen — jump straight to Collect Payment for this member. */
  collectNow() {
    this.router.navigate(['/payments/collect', this.memberId]);
  }

  // ===================== nav =====================
  goToDashboard() {
    // Let the '/dashboard' dispatcher choose by role, so an admin isn't dropped on the
    // partner dashboard.
    this.router.navigate(['/dashboard']);
  }
  onboardAnother() {
    this.restart.emit();
  }
}
